<template>
    <div>
        <card-header title="Create Cart" icon="fa-shopping-cart"/>

        <card-body>
            <card-list>
                <user-tile
                    v-if="quote.user"
                    :card="card"
                    :user="quote.user"
                    @click="$openCard('user', {userId: quote.user.id}, card)"
                />
            </card-list>

            <card-list>
                <subheader-list-item title="Cart Summary" icon="fa-align-left"/>
            </card-list>
            <data-row title="Hours" class="clickable" @click="$copyText(quote.hours)">
                <div class="value">{{ quote.hours }}</div>
            </data-row>
            <data-row title="Total" class="clickable" @click="$copyText(quote.total)">
                <div class="value">{{ quote.total | currency }}</div>
            </data-row>
            <data-row title="Reseller">
                <div class="value">{{ quote.reseller.lastName }}, {{ quote.reseller.firstName }}</div>
            </data-row>
            <template v-if="taxInfo && taxInfo.taxRate > 0">
                <data-row title="Sales Tax Rate">
                    <div class="value">{{ (taxInfo.taxRate * 100).toFixed(3) }}%</div>
                </data-row>
                <data-row title="Sales Tax Amount">
                    <div class="value">{{ taxInfo.taxAmount | currency }}</div>
                </data-row>
            </template>

            <card-list>
                <subheader-list-item title="Reseller Referrals" icon="fa-user-tie"/>

                <card-list-item v-for="r in referrals">
                    <template #icon><i class="fa-fw fas fa-user-tie"></i></template>
                    <template #content>
                        <div class="columns">
                            <div class="column">
                                <strong>{{ r.reseller.last_name }}, {{ r.reseller.first_name }}</strong> ({{ r.calc }})
                            </div>
                            <div class="column is-2 has-text-right">
                                <p class="control">
                                    <button class="button is-primary is-small" @click="removeReferral(r)"><i class="far fa-fw fa-times"></i></button>
                                </p>
                            </div>
                        </div>
                    </template>
                </card-list-item>

                <card-list-item class="pb-5">
                    <template #icon><i class="fa-fw fas fa-plus"></i></template>
                    <template #content>
                        <div class="columns">
                            <div class="column is-6">
                                <b-select expanded placeholder="Choose Reseller" v-model="newReferral.reseller">
                                    <option :value="r" v-for="r in resellers">{{ r.last_name }}, {{ r.first_name }}</option>
                                </b-select>
                            </div>
                            <div class="column is-4">
                                <b-field>
                                    <b-input v-model="newReferral.calc" placeholder="%00 or $00"></b-input>
                                </b-field>
                            </div>
                            <div class="column has-text-right">
                                <p class="control">
                                    <button class="button is-primary pl-2 pr-2" :disabled="!newReferral.reseller || !newReferral.calc" @click="addReferral"><i class="far fa-fw fa-plus"></i></button>
                                </p>
                            </div>
                        </div>
                    </template>
                </card-list-item>

            </card-list>

            <card-list>
                <subheader-list-item title="Courses" icon="fa-chalkboard-teacher"/>

                <card-list-item v-for="c in quote.courses">
                    <template #icon><i class="fa-fw fas fa-chalkboard-teacher"></i></template>
                    <template #content>
                        <div class=""><strong>{{ c.course.name }} {{ c.custom1 }} {{ c.custom2 }}</strong></div>
                        <div>{{ c.config.units }} Hours</div>
                    </template>
                </card-list-item>

            </card-list>
        </card-body>

        <card-footer class="pt-3 pb-3">
            <div class="columns">
                <div class="column has-text-right">
                    <b-button
                        type="is-primary"
                        size="is-small"
                        @click="submit"
                        :disabled="referrals.length === 0"
                    ><i class="fas fa-check mr-2"></i>Create Cart
                    </b-button>
                </div>
            </div>
        </card-footer>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardToolbar from "@/TIER/components/CardToolbar";
    import CardFooter from "@/TIER/components/CardFooter";
    import QuoteForm from "@/cards/reseller/QuoteForm";

    import {client as http} from '../../http_client';
    import DataRow from "../../TIER/components/DataRow";
    import async from "async";
    import CardList from "../../TIER/components/CardList";
    import SubheaderListItem from "../../TIER/components/SubheaderListItem";
    import ContainerListItem from "../../TIER/components/ContainerListItem";
    import UserTile from "../../components/tiles/UserTile";
    import CardListItem from "../../TIER/components/CardListItem";

    export default {
        components: {CardListItem, UserTile, ContainerListItem, SubheaderListItem, CardList, DataRow, QuoteForm, CardFooter, CardToolbar, CardBody, CardHeader},
        props: ['card', 'props'],
        data() {
            return {
                newReferral: {
                    reseller: null,
                    calc: ''
                },
                courses: [],
                resellers: [],
                referrals: [],
                quote: {
                    title: 'Cart Enrollment',
                    status: 'DRAFT',
                    hours: 0,
                    courses: [],
                    user: null,
                    valid: false,
                    reseller: {},
                    term: 0
                },
                taxInfo: null,
                fetchingTax: false
            };
        },
        methods: {
            loadQuote: function() {
                this.$emit('loading', true);

                async.series([
                    (cb) => {
                        http.get('courses').then(response => {
                            this.courses = response.data;
                            cb();
                        });
                    },
                    (cb) => {
                        http.get('users/resellers/0/300').then(response => {
                            this.resellers = response.data;
                            cb();
                        });
                    },
                    (cb) => {
                        http.get('quotes/' + this.props.quoteId, {force: true}).then(response => {
                            response.data.courses = [];
                            response.data.resellers = [];
                            response.data.referrals = [];
                            response.data.valid = true;
                            this.quote = response.data;

                            this.quote.courses = JSON.parse(this.quote.course_choices);

                            for (let i = 0; i < this.quote.courses.length; i++) {
                                const choice = this.quote.courses[i];
                                for (let j = 0; j < this.courses.length; j++) {
                                    const course = this.courses[j];
                                    if (course.id === choice.course_id) {
                                        choice.course = course;

                                        for (let k = 0; k < course.configurations.length; k++) {
                                            const configuration = course.configurations[k];
                                            if (configuration.id === choice.config_id) {
                                                choice.config = configuration;
                                                break;
                                            }
                                        }
                                    }
                                }
                            }

                            for (let i = 0; i < this.resellers.length; i++) {
                                const reseller = this.resellers[i];
                                if (reseller.id === this.quote.reseller.my247ed_id) {
                                    this.newReferral.reseller = reseller;
                                    break;
                                }
                            }
                            cb();
                        }).catch(err => {
                            cb(err);
                        });
                    },
                    (cb) => {
                        // Fetch tax information if user exists
                        if (this.quote.user && this.quote.user.id && this.quote.total) {
                            this.fetchTaxRate(cb);
                        } else {
                            cb();
                        }
                    }
                ], err => {
                    this.$emit('loading', false);
                });
            },
            fetchTaxRate: function(callback) {
                this.fetchingTax = true;

                http.post('quotes/calculate-tax', {
                    userId: this.quote.user.id,
                    amount: parseFloat(this.quote.total)
                }).then(response => {
                    this.taxInfo = response.data;
                    if (callback) callback();
                }).catch(error => {
                    console.error('Tax calculation failed:', error);
                    this.taxInfo = null;
                    if (callback) callback();
                }).finally(() => {
                    this.fetchingTax = false;
                });
            },
            submit: function() {
                this.$emit('loading', true);

                // Prepare referrals data
                const referralsData = [];
                for (let i = 0; i < this.referrals.length; i++) {
                    const referral = this.referrals[i];
                    referralsData.push({
                        referrer: referral.reseller.id,
                        value: referral.calc
                    });
                }

                // Update quote status to CART and add referrals
                const updateData = {
                    id: this.props.quoteId,
                    status: 'CART',
                    term: 0,
                    reseller_referrals: JSON.stringify(referralsData)
                };

                // Add tax information if available
                if (this.taxInfo && this.taxInfo.success && this.taxInfo.taxRate > 0) {
                    updateData.tax_rate = this.taxInfo.taxRate;
                    updateData.tax_state_rate = this.taxInfo.stateRate || 0;
                    updateData.tax_local_rate = this.taxInfo.localRate || 0;
                    updateData.tax_jurisdiction = this.taxInfo.jurisdiction;
                    updateData.tax_location_code = this.taxInfo.locationCode;
                    updateData.tax_result_code = this.taxInfo.resultCode;
                    updateData.tax_amount = this.taxInfo.taxAmount;
                    updateData.tax_calculated_at = new Date().toISOString().slice(0, 19).replace('T', ' ');
                }

                http.put('quotes/' + this.props.quoteId, updateData, {force: true}).then(() => {
                    this.$closeCard(this.card);
                    this.$openCard('reseller-quotes', {}, null);
                    this.$reloadCard('reseller-quotes');
                }).catch(err => {
                    console.error('Error creating cart:', err);
                    this.$emit('loading', false);
                });
            },
            addReferral: function() {
                this.referrals.push(this.newReferral);
                this.newReferral = {reseller: null, calc: ''};
            },
            removeReferral: function(r) {
                this.referrals.splice(this.referrals.indexOf(r), 1);
            }
        },
        mounted() {
            this.loadQuote();
        }
    };
</script>

<style lang="scss" scoped>
    .clickable {
        cursor: pointer;
    }

    .context {
        font-size: 0.85rem;
        color: #666;
        background-color: #f4f4f4;
        padding: 3px 5px;
        border-radius: 5px;
        margin: 10px 3px;
    }
</style>
